import posthog from 'posthog-js';
import { getSessionId } from './observability/requestId';
import { Sentry } from './sentry';

const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY;
const POSTHOG_HOST = import.meta.env.VITE_POSTHOG_HOST || 'https://us.i.posthog.com';
// Optional first-party reverse-proxy path (e.g. '/ingest') to survive ad-blockers.
// Only used when set AND the matching Vercel rewrites exist (see vercel.json).
const POSTHOG_PROXY = import.meta.env.VITE_POSTHOG_PROXY as string | undefined;
// When 'true', do not capture until the user opts in (requires a consent UI to call optInAnalytics()).
const REQUIRE_CONSENT = import.meta.env.VITE_REQUIRE_CONSENT === 'true';

/**
 * Initialize PostHog analytics.
 * Call this once when the app starts.
 */
export function initPostHog() {
  if (POSTHOG_KEY && typeof window !== 'undefined') {
    posthog.init(POSTHOG_KEY, {
      api_host: POSTHOG_PROXY || POSTHOG_HOST,
      ui_host: POSTHOG_PROXY ? 'https://us.posthog.com' : undefined,

      // Use 2025 defaults for modern SPA behavior
      defaults: '2025-11-30',

      // Cost/PII posture, set EXPLICITLY (don't rely on the dated defaults bundle):
      // only create person profiles for identified users, so anonymous traffic doesn't
      // inflate billed events or create stub profiles.
      person_profiles: 'identified_only',

      // Page tracking - 'history_change' captures SPA navigation automatically
      capture_pageview: 'history_change',
      capture_pageleave: 'if_capture_pageview',

      // Session recording — privacy-first: mask ALL inputs AND all rendered text so
      // customer emails / team-member names never enter a recording.
      session_recording: {
        maskAllInputs: true,
        maskTextSelector: '*',
      },
      // Do NOT record console logs — they contained emails, auth events and OAuth
      // error bodies (audit finding console-log-recording-pii). Sentry owns error context.
      enable_recording_console_log: false,

      // Web vitals for Core Web Vitals metrics (LCP, FID, CLS, FCP)
      capture_performance: {
        web_vitals: true,
        network_timing: true,
      },

      // Keep autocapture off - we have intentional custom events
      autocapture: false,

      // Persistence for cross-session tracking
      persistence: 'localStorage',

      // Compliance: honor Do Not Track. Optionally require explicit consent.
      respect_dnt: true,
      opt_out_capturing_by_default: REQUIRE_CONSENT,
    });

    // Correlation spine: every event carries the per-tab session id + release, so PostHog
    // joins the same session as Sentry/Axiom/BetterStack.
    posthog.register({
      session_id: getSessionId(),
      release: import.meta.env.VITE_RELEASE,
    });

    // Replay linkage: stamp PostHog's own session id on Sentry so an error can pivot
    // straight to the matching PostHog replay (PostHog owns replay; Sentry's is off).
    const phSessionId = posthog.get_session_id?.();
    if (phSessionId) Sentry.setTag('posthog_session_id', phSessionId);
  }
}

/** Call from a consent banner's "Accept" action when VITE_REQUIRE_CONSENT is on. */
export function optInAnalytics() {
  if (!POSTHOG_KEY) return;
  posthog.opt_in_capturing();
}

/** Call from a consent banner's "Decline" action. */
export function optOutAnalytics() {
  if (!POSTHOG_KEY) return;
  posthog.opt_out_capturing();
}

/**
 * Identify a user after login.
 */
export function identifyUser(userId: string, properties?: Record<string, unknown>) {
  if (!POSTHOG_KEY) return;
  posthog.identify(userId, properties);
}

/**
 * Associate the current user with their organization group (enables B2B group analytics).
 */
export function setOrganizationContext(orgId: string, orgSlug: string) {
  if (!POSTHOG_KEY || !orgId) return;
  posthog.group('organization', orgId, { slug: orgSlug });
}

/**
 * Reset user identity on logout.
 */
export function resetUser() {
  if (!POSTHOG_KEY) return;
  posthog.reset();
}

/**
 * Track an event with properties.
 */
export function trackEvent(event: string, properties?: Record<string, unknown>) {
  if (!POSTHOG_KEY) return;
  posthog.capture(event, properties);
}

// Re-export posthog for direct access if needed (e.g., PostHogProvider)
export { posthog };
